import React, { useState, useEffect, useRef } from "react";
import photoFull from "../assets/newest-version/Full Front - Everything extended.jpg";
import photoBasic from "../assets/newest-version/Full - nothing extended just basic.jpg";
import photoCards from "../assets/newest-version/Credit Cards Extended.jpg";
import photoId from "../assets/newest-version/ID Card Holder.jpg";
import photoTracking from "../assets/newest-version/Integrated Tracking Card.jpg";
import photoCash from "../assets/newest-version/Back - with cash.jpg";
import photoBack from "../assets/newest-version/Back No Cash.jpg";
import photoKeysFocus from "../assets/newest-version/Back - Keys Focus.jpg";

const futura = "'Futura', sans-serif";

// Real-world photos of the metal Brik, shared by the home hero and the
// product page gallery so there's a single source of truth.
export const BRIK_PHOTOS = [
  {
    src: photoFull,
    alt: "The metal Brik with ID, cards, and keys all extended",
    caption:
      "Everything on deck. ID up front, cards fanned behind it, keys clipped on. Your whole carry in one piece.",
  },
  {
    src: photoBasic,
    alt: "The metal Brik closed, with keys attached",
    caption:
      "Closed and pocket-ready. A slim metal slab that disappears into your pocket.",
  },
  {
    src: photoCards,
    alt: "Credit cards fanned out of the metal Brik",
    caption: "Push to fan your cards out. Grab the one you need, no digging.",
  },
  {
    src: photoId,
    alt: "The front ID window of the metal Brik",
    caption:
      "Front ID window. Badge in, tap out at the door without taking it off.",
  },
  {
    src: photoTracking,
    alt: "The integrated tracking card sliding into the metal Brik",
    caption:
      "The integrated tracking card slips right in. Ring your wallet and see its location from your phone.",
  },
  {
    src: photoCash,
    alt: "Cash held in the back money clip of the metal Brik",
    caption: "The back money clip pins your bills down tight, even on the move.",
  },
  {
    src: photoBack,
    alt: "The machined metal back of the Brik, etched with the brik",
    caption:
      "Machined from solid metal and laser-etched with the brik. Built to outlast the original.",
  },
  {
    src: photoKeysFocus,
    alt: "Close-up of the keyring integration on the metal Brik",
    caption:
      "Keyring integration keeps your keys on the wallet and quiet in your pocket.",
  },
];

/**
 * Two looks:
 *  - default: contained image + caption + thumbnail strip (product page).
 *  - fill: a full-bleed, crossfading, auto-rotating carousel with dot
 *    ("bubble") navigation and an overlaid caption (home hero).
 *
 * Props:
 *  - fill         render edge-to-edge inside a sized parent (cover)
 *  - autoPlayMs   advance every N ms (0 = off); pauses on hover
 *  - captionColor caption text color (non-fill mode)
 *  - children     extra controls rendered under the nav (e.g. a 3D button)
 */
const BrikPhotoGallery = ({
  fill = false,
  autoPlayMs = 0,
  imageHeight = "58vh",
  imageMinHeight = "400px",
  imageMaxWidth = "100%",
  captionColor = "#ddd",
  children,
}) => {
  const [activePhoto, setActivePhoto] = useState(0);
  const [paused, setPaused] = useState(false);
  const photo = BRIK_PHOTOS[activePhoto];

  // Keep the latest index in a ref so the interval callback never goes stale.
  const idxRef = useRef(activePhoto);
  idxRef.current = activePhoto;

  useEffect(() => {
    if (!autoPlayMs || paused) return undefined;
    const id = setInterval(() => {
      setActivePhoto((idxRef.current + 1) % BRIK_PHOTOS.length);
    }, autoPlayMs);
    return () => clearInterval(id);
  }, [autoPlayMs, paused]);

  // ===== Full-bleed auto-rotating hero carousel =====
  if (fill) {
    const arrowStyle = (side) => ({
      position: "absolute",
      top: "50%",
      [side]: "16px",
      transform: "translateY(-50%)",
      width: "46px",
      height: "46px",
      borderRadius: "999px",
      border: "1px solid rgba(255,255,255,0.25)",
      background: "rgba(0,0,0,0.4)",
      color: "white",
      fontSize: "26px",
      lineHeight: 1,
      fontFamily: futura,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingBottom: "3px",
      backdropFilter: "blur(2px)",
      zIndex: 2,
    });
    return (
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          background: "#0c0c0c",
        }}
      >
        {BRIK_PHOTOS.map((p, i) => (
          <img
            key={p.src}
            src={p.src}
            alt={i === activePhoto ? p.alt : ""}
            aria-hidden={i === activePhoto ? undefined : true}
            width={1024}
            height={1024}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: i === activePhoto ? 1 : 0,
              transition: "opacity 0.9s ease",
            }}
          />
        ))}

        {/* Prev / next arrows */}
        <button
          type="button"
          onClick={() =>
            setActivePhoto(
              (i) => (i - 1 + BRIK_PHOTOS.length) % BRIK_PHOTOS.length
            )
          }
          aria-label="Previous photo"
          style={arrowStyle("left")}
        >
          ‹
        </button>
        <button
          type="button"
          onClick={() => setActivePhoto((i) => (i + 1) % BRIK_PHOTOS.length)}
          aria-label="Next photo"
          style={arrowStyle("right")}
        >
          ›
        </button>

        {/* Bottom scrim for legible caption + controls */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            padding: "60px 24px 22px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "14px",
            background:
              "linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0) 100%)",
          }}
        >
          <p
            style={{
              margin: 0,
              textAlign: "center",
              color: "#f2f2f2",
              fontSize: "15px",
              fontFamily: futura,
              lineHeight: 1.55,
              maxWidth: "440px",
              textShadow: "0 1px 6px rgba(0,0,0,0.5)",
            }}
          >
            {photo.caption}
          </p>

          {/* Bubble navigation */}
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {BRIK_PHOTOS.map((p, i) => (
              <button
                key={p.src}
                onClick={() => setActivePhoto(i)}
                aria-label={`View photo ${i + 1}: ${p.alt}`}
                aria-current={i === activePhoto}
                style={{
                  width: i === activePhoto ? "30px" : "11px",
                  height: "11px",
                  borderRadius: "999px",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background:
                    i === activePhoto ? "#ff7d1a" : "rgba(255,255,255,0.5)",
                  transition: "all 0.3s ease",
                }}
              />
            ))}
          </div>

          {children}
        </div>
      </div>
    );
  }

  // ===== Default: contained image + caption + thumbnail strip =====
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <img
        src={photo.src}
        alt={photo.alt}
        width={1024}
        height={1024}
        fetchPriority="high"
        style={{
          width: "100%",
          maxWidth: imageMaxWidth,
          height: imageHeight,
          minHeight: imageMinHeight,
          objectFit: "contain",
          display: "block",
          borderRadius: "12px",
        }}
      />
      <p
        style={{
          textAlign: "center",
          color: captionColor,
          fontSize: "14px",
          fontFamily: futura,
          lineHeight: 1.6,
          maxWidth: "460px",
          margin: "16px auto 0",
        }}
      >
        {photo.caption}
      </p>
      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: "18px",
        }}
      >
        {BRIK_PHOTOS.map((p, i) => (
          <button
            key={p.src}
            onClick={() => setActivePhoto(i)}
            aria-label={p.alt}
            style={{
              width: "54px",
              height: "54px",
              padding: 0,
              borderRadius: "8px",
              overflow: "hidden",
              cursor: "pointer",
              background: "#1a1a1a",
              border:
                i === activePhoto ? "2px solid #ff7d1a" : "2px solid #444",
              flexShrink: 0,
            }}
          >
            <img
              src={p.src}
              alt=""
              width={54}
              height={54}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </button>
        ))}
      </div>
      {children}
    </div>
  );
};

export default BrikPhotoGallery;
